import { useCallback, useState } from 'react'

/**
 * Custom hook to manage a transition state.
 *
 * This hook helps to keep the UI responsive by deferring the execution of a callback function
 * using `setTimeout` with a 0 timeout.
 * This allows the UI to update before the callback is executed, effectively unblocking the main thread.
 *
 * @returns {[boolean, (callback: Function, timeout?: number) => void]}
 *   - isPending: A boolean indicating if a transition is in progress.
 *   - startTransition: A function to start the transition with an optional timeout.
 */
export const useCustomTransition = (): [
    boolean,
    (callback: any, timeout?: number) => void | Promise<any>
] => {
    const [isPending, setIsPending] = useState(false)

    const startTransition = useCallback((callback, timeout = 0) => {
        setIsPending(true)

        const executeCallbackTask = setTimeout(() => {
            callback()
            setIsPending(false)
            clearTimeout(executeCallbackTask)
        }, timeout)
    }, [])

    return [isPending, startTransition]
}
